import React, { useState, useRef, useEffect } from 'react';
import './MedicalAssistantPiP.scss';
import { useI18n } from '../contexts/I18nContext';
import chatAddIcon from '../../assets/chat-add.svg';
import sendIcon from '../../assets/send.svg';
import trashIcon from '../../assets/field-icons/trash-icon.svg';

interface LocalMessage {
  id: string;
  sender: 'user' | 'assistant';
  text: string;
  timestamp: Date;
}

const MedicalAssistantPiP: React.FC = () => {
  const { t, language } = useI18n();
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState<LocalMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Автоскролл к последнему сообщению
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: LocalMessage = {
      id: `user_${Date.now()}`,
      sender: 'user',
      text: inputValue.trim(),
      timestamp: new Date()
    };

    // Добавляем сообщение пользователя
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Запрос к медицинскому API
      const response = await fetch('https://gptdb.telemedhub.io/api/v2/sendMessage/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic TGF6eURvYzo3OGU2YTUyYy05N2IzLTQzNjMtODJkZi0xNDVkNGE1NTJiNmU='
        },
        body: JSON.stringify({
          prompt: 'lazydoc-copilot',
          messages: [
            {
              role: 'user',
              content: inputValue.trim()
            }
          ]
        })
      });

      if (response.ok) {
        const data = await response.json();

        const assistantMessage: LocalMessage = {
          id: `assistant_${Date.now()}`,
          sender: 'assistant',
          text: data.response || t('copilot.errorResponse'),
          timestamp: new Date()
        };

        setMessages(prev => [...prev, assistantMessage]);
      } else {
        const errorData = await response.json();
        console.error('Ошибка запроса к ИИ:', errorData);

        const errorMessage: LocalMessage = {
          id: `error_${Date.now()}`,
          sender: 'assistant',
          text: t('copilot.errorAssistant'),
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error('Ошибка запроса к медицинскому помощнику:', error);

      const errorMessage: LocalMessage = {
        id: `error_${Date.now()}`,
        sender: 'assistant',
        text: t('copilot.errorAssistant'),
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
  };

  return (
    <div className="medical-assistant-pip">
      {/* Заголовок */}
      <div className="pip-header">
        <div className="pip-title">
          <img src={chatAddIcon} alt="Copilot" className="pip-copilot-icon" />
          <span>{t('copilot.title')}</span>
        </div>
        <button
          className="pip-clear-btn"
          onClick={clearChat}
          title={t('copilot.clearChat')}
        >
          <img src={trashIcon} alt="Clear" className="pip-clear-icon" />
        </button>
      </div>

      {/* Список сообщений */}
      <div className="pip-messages">
        {messages.length === 0 ? (
          <div className="pip-no-chat">
            <img src={chatAddIcon} alt="Chat" className="pip-no-chat-icon" />
            <p>{t('copilot.askQuestion')}</p>
            <p className="pip-subtitle">{t('copilot.localHint')}</p>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <div
                key={message.id}
                className={`pip-message ${message.sender === 'user' ? 'user' : 'assistant'}`}
              >
                <div className="pip-message-content">
                  {message.text}
                </div>
                <div className="pip-message-time">
                  {message.timestamp.toLocaleTimeString(language === 'ru' ? 'ru-RU' : 'en-US', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="pip-message assistant">
                <div className="pip-message-content pip-typing">
                  <span className="pip-typing-dots">
                    <span>.</span><span>.</span><span>.</span>
                  </span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Поле ввода */}
      <div className="pip-input">
        <textarea
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={t('copilot.askPlaceholder')}
          rows={1}
          className="pip-input-field"
          disabled={isLoading}
        />
        <button
          className="pip-send-btn"
          onClick={handleSendMessage}
          disabled={!inputValue.trim() || isLoading}
        >
          {isLoading ? (
            <div className="pip-loading-spinner"></div>
          ) : (
            <img src={sendIcon} alt="Send" className="pip-send-icon" />
          )}
        </button>
      </div>
    </div>
  );
};

export default MedicalAssistantPiP;